import React, {useEffect, useState} from "react";
import {
  Board,
  BoardHeader,
  BoardSearchResult,
  CalendarBox,
  CalendarIcon,
  CancelButton,
  CustomDatePicker,
  DateContainer,
  Input,
  InputLabel,
  SubmitContainer,
  ValidationScript
} from "../../../assets/GlobalStyles";
import {AdverInfo} from "../styles/common";
import Select from "react-select";
import {useAtom, useSetAtom} from "jotai";
import {stepCampaignAtom} from "../entity";
import {
  Controller,
  useFormContext
} from "react-hook-form";
import TimeTable from "../../../components/modal/TimeTable";
import {
  biddingTypeAll,
  campaignBasicInfoAtom
} from "../entity/Info";
import {
  selBudgetTimeDetailInfo,
  selBudgetTimeList
} from "../../../services/settings/BudgetTimeAxios";
import {
  timeBudgetDetailDataAtom
} from "../../settings/entity/BudgetTime";
import {
  cancelCampaignBudgetSchedule,
  reserveCampaignBudget,
  selBudgetInfo,
  selCampaignBudgetScheduleHistory,
  updateCampaignBudget
} from "../../../services/campaign/BudgetAxios";
import {
  campaignBudgetInfoAtom
} from "../entity/Budget";
import {
  useLocation,
  useNavigate
} from "react-router-dom";
import {toast} from "react-toastify";
import {useResetAtom} from "jotai/utils";
import {confirmAlert} from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  decimalFormat,
  removeStr
} from "../../../common/StringUtils";
import {
  campaignGroupInfoAtom
} from "../entity/Group";
import queryString from "query-string";
import styled from "styled-components";
import ko from "date-fns/locale/ko";
import {CampaignStepForm, CampaignStepSidebar} from "./CampaignStepDesign";
import CampaignSettingSummary from "../CampaignSettingSummary";

const DEFAULT_BUDGET_SCHEDULE_DRAFT = {
  scheduleId: null,
  scheduledAt: ""
};

const BUDGET_SCHEDULE_STATUS = {
  WAITING: "적용 대기",
  PROCESSING: "적용 처리 중",
  APPLIED: "적용 완료",
  CANCELLED: "예약 취소",
  FAILED: "적용 실패"
};

/**
 * Date 또는 서버 날짜 값을 YYYY-MM-DD 형식으로 변환한다.
 */
const toDateValue = value => {
  if (!value) {
    return "";
  }

  if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    return value;
  }

  if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T/.test(value)
  ) {
    return value.substring(0, 10);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * YYYY-MM-DD 값을 로컬 Date 객체로 변환한다.
 */
const toDateObject = value => {
  const dateValue = toDateValue(value);

  if (!dateValue) {
    return null;
  }

  const [year, month, day] = dateValue.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * 예약 가능한 최소 날짜.
 * 예약은 날짜 단위로 적용되므로 내일부터 선택 가능하게 한다.
 */
const getTomorrow = () => {
  const tomorrow = new Date();

  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return tomorrow;
};

const extractScheduleList = response => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.schedules)) {
    return response.schedules;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.dataEntity?.data)) {
    return response.dataEntity.data;
  }

  return [];
};

const getScheduleId = schedule =>
    schedule?.scheduleId ?? schedule?.id;

const getScheduleDate = schedule =>
    toDateValue(
        schedule?.scheduledDate ??
        schedule?.scheduledAt
    );

const isEditableSchedule = schedule =>
    schedule?.status === "WAITING";

const isActiveSchedule = schedule =>
    ["WAITING", "PROCESSING"].includes(
        schedule?.status
    );


function BudgetScheduleSetting({
  isEditMode,
  isScheduleMode,
  onChangeScheduleMode,
  budgetScheduleDraft,
  setBudgetScheduleDraft,
  scheduleHistory,
  errors,
  clearErrors,
  onSaveSchedule,
  onEditSchedule,
  onCancelSchedule,
  onResetSchedule,
  isSavingSchedule
}) {
  const isEditing =
      budgetScheduleDraft.scheduleId !== null;

  const selectedScheduledDate =
      toDateObject(
          budgetScheduleDraft.scheduledAt
      );

  const handleChangeScheduledAt = date => {
    setBudgetScheduleDraft(prev => ({
      ...prev,
      scheduledAt: date
          ? toDateValue(date)
          : ""
    }));

    clearErrors("scheduledAt");
  };

  return (
      <SettingCard>
        <SectionHeader>
          <SectionTitle>
            예산 예약 설정
          </SectionTitle>

          <SectionGuide>
            현재 설정한 예산과 입찰 정보를
            지정한 날짜에 적용합니다.
          </SectionGuide>
        </SectionHeader>

        <InnerBox>
          <FieldRow>
            <FieldLabel>
              예약 설정
            </FieldLabel>

            <FieldContent>
              <ScheduleControlRow>
                <ScheduleModeButton
                    type="button"
                    $active={isScheduleMode}
                    disabled={
                        !isEditMode ||
                        isSavingSchedule
                    }
                    onClick={() =>
                        onChangeScheduleMode(
                            !isScheduleMode
                        )
                    }
                >
                  <ScheduleModeText
                      $active={isScheduleMode}
                  >
                    {isScheduleMode
                        ? "ON"
                        : "OFF"}
                  </ScheduleModeText>

                  <ScheduleModeKnob
                      $active={isScheduleMode}
                  />
                </ScheduleModeButton>

                {isEditMode &&
                    isScheduleMode && (
                        <>
                          <ScheduleDatePickerWrap>
                            <DateContainer>
                              <CalendarBox>
                                <CalendarIcon />
                              </CalendarBox>

                              <CustomDatePicker
                                  selected={
                                    selectedScheduledDate
                                  }
                                  placeholderText="예약 적용 날짜를 선택해주세요."
                                  minDate={getTomorrow()}
                                  onChange={
                                    handleChangeScheduledAt
                                  }
                                  dateFormat="yyyy-MM-dd"
                                  locale={ko}
                                  isClearable={false}
                                  monthsShown={2}
                                  openToDate={
                                      selectedScheduledDate ??
                                      getTomorrow()
                                  }
                              />
                            </DateContainer>
                          </ScheduleDatePickerWrap>

                          <ScheduleButtonGroup>
                            <SchedulePrimaryButton
                                type="button"
                                disabled={
                                  isSavingSchedule
                                }
                                onClick={
                                  onSaveSchedule
                                }
                            >
                              {isSavingSchedule
                                  ? "처리 중"
                                  : isEditing
                                      ? "예약 수정"
                                      : "예약 추가"}
                            </SchedulePrimaryButton>

                            {(isEditing ||
                                budgetScheduleDraft
                                    .scheduledAt) && (
                                <ScheduleSecondaryButton
                                    type="button"
                                    disabled={
                                      isSavingSchedule
                                    }
                                    onClick={
                                      onResetSchedule
                                    }
                                >
                                  편집 취소
                                </ScheduleSecondaryButton>
                            )}
                          </ScheduleButtonGroup>
                        </>
                    )}

                <ScheduleDescription>
                  {!isEditMode
                      ? "캠페인 생성 완료 후 예약 설정을 사용할 수 있습니다."
                      : isScheduleMode
                          ? "선택한 날짜 00시에 현재 예산 설정이 자동 적용됩니다."
                          : "예약 설정이 꺼져 있습니다. 현재 예산을 직접 수정합니다."}
                </ScheduleDescription>

                {isEditing && (
                    <EditingBadge>
                      예약 수정 중
                    </EditingBadge>
                )}
              </ScheduleControlRow>

              {errors.scheduledAt && (
                  <ValidationMessage>
                    {
                      errors.scheduledAt
                          .message
                    }
                  </ValidationMessage>
              )}
            </FieldContent>
          </FieldRow>

          {isEditMode &&
              isScheduleMode && (
                  <FieldRow>
                    <FieldLabel>
                      예약 목록

                      <FieldLabelDescription>
                        설정된 예약 내역을 확인하고
                        관리할 수 있습니다.
                      </FieldLabelDescription>
                    </FieldLabel>

                    <FieldContent>
                      <ScheduleListTop>
                        <ScheduleDescription>
                          적용 대기 중인 예약은 수정하거나
                          취소할 수 있습니다.
                        </ScheduleDescription>

                        <SelectedCount>
                          {scheduleHistory.length}
                          건
                        </SelectedCount>
                      </ScheduleListTop>

                      <ScheduleHistoryBox>
                        <ScheduleHeader>
                          <span>적용 날짜</span>
                          <span>일일 예산</span>
                          <span>PC / MOBILE</span>
                          <span>입찰 단가</span>
                          <span>상태</span>
                          <span>관리</span>
                        </ScheduleHeader>

                        <ScheduleBody>
                          {scheduleHistory.length ===
                          0 ? (
                              <ScheduleEmpty>
                                등록된 예산 예약이 없습니다.
                              </ScheduleEmpty>
                          ) : (
                              scheduleHistory.map(
                                  schedule => {
                                    const scheduleId =
                                        getScheduleId(
                                            schedule
                                        );

                                    return (
                                        <ScheduleRow
                                            key={
                                                scheduleId ??
                                                getScheduleDate(
                                                    schedule
                                                )
                                            }
                                        >
                            <span>
                              {getScheduleDate(
                                  schedule
                              ) || "-"}
                            </span>

                                          <span>
                              {schedule
                                  .infiniteBudgetYn ===
                              "Y"
                                  ? "무제한"
                                  : `${decimalFormat(
                                      schedule.dailyAvgBudget ??
                                      0
                                  )}원`}
                            </span>

                                          <span>
                              {schedule
                                  .infiniteBudgetYn ===
                              "Y"
                                  ? "-"
                                  : `${decimalFormat(
                                      schedule.pcBudget ??
                                      0
                                  )}원 / ${decimalFormat(
                                      schedule.mobBudget ??
                                      0
                                  )}원`}
                            </span>

                                          <span>
                              {decimalFormat(
                                  schedule.biddingPrice ??
                                  0
                              )}
                                            원
                            </span>

                                          <span>
                              <ScheduleStatus
                                  $status={
                                    schedule.status
                                  }
                              >
                                {BUDGET_SCHEDULE_STATUS[
                                        schedule.status
                                        ] ??
                                    schedule.status}
                              </ScheduleStatus>
                            </span>

                                          <ScheduleActions>
                                            {isEditableSchedule(
                                                schedule
                                            ) && (
                                                <>
                                                  <ScheduleSecondaryButton
                                                      type="button"
                                                      onClick={() =>
                                                          onEditSchedule(
                                                              schedule
                                                          )
                                                      }
                                                  >
                                                    수정
                                                  </ScheduleSecondaryButton>

                                                  <ScheduleSecondaryButton
                                                      type="button"
                                                      onClick={() =>
                                                          onCancelSchedule(
                                                              schedule
                                                          )
                                                      }
                                                  >
                                                    취소
                                                  </ScheduleSecondaryButton>
                                                </>
                                            )}
                                          </ScheduleActions>
                                        </ScheduleRow>
                                    );
                                  }
                              )
                          )}
                        </ScheduleBody>
                      </ScheduleHistoryBox>
                    </FieldContent>
                  </FieldRow>
              )}
        </InnerBox>
      </SettingCard>
  );
}

export function CampaignTwo() {
  const setStepCampaign = useSetAtom(stepCampaignAtom);

  const [campaignBasicInfo, setCampaignBasicInfo] =
      useAtom(campaignBasicInfoAtom);

  const [campaignBudgetInfo, setCampaignBudgetInfo] =
      useAtom(campaignBudgetInfoAtom);

  const [timeBudgetDetailDataState, setTimeBudgetDetailDataState] =
      useAtom(timeBudgetDetailDataAtom);

  const [budgetRateChk, setBudgetRateChk] = useState(false);
  const [budgetTimeListState, setBudgetTimeListState] = useState([]);
  const [biddingType] = useState(biddingTypeAll);
  const [budgetScheduleDraft, setBudgetScheduleDraft] =
      useState(DEFAULT_BUDGET_SCHEDULE_DRAFT);
  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [isScheduleMode, setIsScheduleMode] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isSavingBudget, setIsSavingBudget] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    formState: {errors},
    clearErrors
  } = useFormContext();

  const resetGroupInfo = useResetAtom(campaignGroupInfoAtom);

  const state =
      location.search !== ""
          ? queryString.parse(location.search)
          : location.state;

  const isEditMode = Boolean(state?.campaignId);

  const campaignId =
      state?.campaignId ?? campaignBasicInfo?.campaignId;

  const userId =
      state?.userId || campaignBasicInfo?.userId;

  const loadBudgetTimeDetail = async budgetTimeId => {
    if (!userId || !budgetTimeId) {
      return;
    }

    const response = await selBudgetTimeDetailInfo(
        userId,
        budgetTimeId
    );

    setTimeBudgetDetailDataState(response);
  };

  const applyBudgetResponse = async response => {
    if (!response) {
      return;
    }

    const dailyAvgBudget = Number(response.dailyAvgBudget ?? 0);
    const pcBudget = Number(response.pcBudget ?? 0);

    const budgetRate =
        dailyAvgBudget !== 0
            ? Math.round(pcBudget * 100 / dailyAvgBudget)
            : 50;

    const data = {
      ...response,
      budgetRate
    };

    setBudgetRateChk(
        Number(response.pcBudget) === 0 ||
        Number(response.mobBudget) === 0
    );

    setCampaignBudgetInfo(data);
    reset(data);

    await loadBudgetTimeDetail(data.budgetTimeId);
  };

  const loadCurrentBudget = async () => {
    if (!campaignId) {
      throw new Error(
          "campaignId가 존재하지 않습니다."
      );
    }

    const response =
        await selBudgetInfo(campaignId);

    if (!response) {
      throw new Error(
          "현재 예산 정보를 조회하지 못했습니다."
      );
    }

    await applyBudgetResponse(response);
  };

  const loadScheduleHistory = async () => {
    if (!isEditMode || !campaignId) {
      setScheduleHistory([]);
      return [];
    }

    const response =
        await selCampaignBudgetScheduleHistory(
            campaignId
        );

    if (response === false || response === null) {
      throw new Error(
          "예산 예약 목록을 조회하지 못했습니다."
      );
    }

    const schedules =
        extractScheduleList(response)
        .slice()
        .sort((a, b) => {
          const firstDate =
              toDateObject(
                  getScheduleDate(a)
              );

          const secondDate =
              toDateObject(
                  getScheduleDate(b)
              );

          return (
              (firstDate?.getTime() ?? 0) -
              (secondDate?.getTime() ?? 0)
          );
        });

    setScheduleHistory(schedules);

    return schedules;
  };


  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      setBudgetScheduleDraft(
          DEFAULT_BUDGET_SCHEDULE_DRAFT
      );

      clearErrors("scheduledAt");

      if (!userId) {
        return;
      }

      try {
        const budgetTimeResponse =
            await selBudgetTimeList(userId);

        if (!mounted) {
          return;
        }

        const timeGroups =
            budgetTimeResponse?.timeGroups ?? [];

        const budgetTimeList =
            timeGroups.map(data => ({
              value: data.id,
              label: data.groupName
            }));

        setBudgetTimeListState(
            budgetTimeList
        );

        const shouldLoadBudget =
            isEditMode ||
            [
              "STEP2_BUDGET",
              "STEP3_INVENTORY",
              "STEP4_CREATIVE",
              "COMPLETED"
            ].includes(
                campaignBasicInfo.step
            );

        if (
            shouldLoadBudget &&
            campaignId
        ) {
          await loadCurrentBudget();

          if (isEditMode) {
            const schedules =
                await loadScheduleHistory();

            setIsScheduleMode(
                schedules.some(
                    isActiveSchedule
                )
            );
          } else {
            setIsScheduleMode(false);
            setScheduleHistory([]);
          }

          return;
        }

        setIsScheduleMode(false);
        setScheduleHistory([]);

        const defaultBudgetTimeId =
            budgetTimeList[0]?.value;

        if (defaultBudgetTimeId) {
          setCampaignBudgetInfo(prev => ({
            ...prev,
            budgetTimeId:
                prev.budgetTimeId ??
                defaultBudgetTimeId
          }));

          await loadBudgetTimeDetail(
              defaultBudgetTimeId
          );
        }
      } catch (error) {
        console.error(
            "예산 화면 초기화 실패:",
            error
        );

        toast.error(
            "예산 정보를 불러오는 중 오류가 발생했습니다.",
            {
              toastId:
                  "initializeCampaignBudgetError"
            }
        );
      }

    };

    initialize();

    return () => {
      mounted = false;
    };

// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search, userId, campaignId]);

  const handleChangeBudgetTimes = selectedBudgetTime => {
    if (!selectedBudgetTime) {
      return;
    }

    setCampaignBudgetInfo(prev => ({
      ...prev,
      budgetTimeId: selectedBudgetTime.value
    }));

    loadBudgetTimeDetail(
        selectedBudgetTime.value
    );

    clearErrors("budgetTimeId");
  };

  const handleChangePriceEvent = value => {
    const numberValue = removeStr(value);

    const biddingPrice =
        numberValue !== ""
            ? parseInt(numberValue, 10)
            : 0;

    setCampaignBudgetInfo(prev => ({
      ...prev,
      biddingPrice
    }));

    clearErrors("biddingPrice");
  };

  const handleBiddingType = selectedBiddingType => {
    if (!selectedBiddingType) {
      return;
    }

    setCampaignBudgetInfo(prev => ({
      ...prev,
      biddingType: selectedBiddingType.value
    }));
  };

  const handleCheckInfiniteBudget = event => {
    const infiniteBudgetYn = event.target.checked ? "Y" : "N";

    clearErrors("dailyAvgBudget");

    setCampaignBudgetInfo(prev => ({
      ...prev,
      infiniteBudgetYn
    }));
  };

  const handleChangeDailyBudget = value => {
    const numberValue = removeStr(value);

    const dailyAvgBudget =
        numberValue !== ""
            ? parseInt(numberValue, 10)
            : 0;

    const previousBudgetRate =
        campaignBudgetInfo.budgetRate !== 0
            ? campaignBudgetInfo.budgetRate
            : 50;

    clearErrors("dailyAvgBudget");

    setCampaignBudgetInfo(prev => ({
      ...prev,
      dailyAvgBudget,
      pcBudget:
          dailyAvgBudget * previousBudgetRate / 100,
      mobBudget:
          dailyAvgBudget * (100 - previousBudgetRate) / 100,
      budgetRate: previousBudgetRate
    }));
  };

  const validateDailyBudget = () => {
    if (campaignBudgetInfo.infiniteBudgetYn === "Y") {
      clearErrors("dailyAvgBudget");
      return true;
    }

    const dailyAvgBudget = Number(campaignBudgetInfo.dailyAvgBudget);

    if (
        dailyAvgBudget < 100 ||
        dailyAvgBudget % 100 !== 0
    ) {
      setError("dailyAvgBudget", {
        type: "validate",
        message: "일일 평균 예산을 최소 100원 단위로 설정해주세요."
      });

      return false;
    }

    clearErrors("dailyAvgBudget");
    return true;
  };

  const validateBudgetInfo = () => {
    let valid = true;

    if (!validateDailyBudget()) {
      valid = false;
    }
    if (!campaignBudgetInfo.biddingType) {
      toast.error("입찰 방식을 선택해주세요.", {
        toastId: "campaignBudgetBiddingTypeRequired"
      });

      valid = false;
    }

    if (!campaignBudgetInfo.budgetTimeId) {
      setError("budgetTimeId", {
        type: "required",
        message: "시간대 예산을 선택해주세요."
      });

      valid = false;
    }

    if (
        !campaignBudgetInfo.biddingPrice ||
        Number(campaignBudgetInfo.biddingPrice) <= 0
    ) {
      setError("biddingPrice", {
        type: "required",
        message: "단가를 입력해주세요."
      });

      valid = false;
    }

    return valid;
  };

  const handleChangePcBudget = value => {
    const numberValue = removeStr(value);

    const pcBudget =
        numberValue !== ""
            ? parseInt(numberValue, 10)
            : 0;

    if (!validateDailyBudget()) {
      return;
    }

    if (pcBudget > Number(campaignBudgetInfo.dailyAvgBudget)) {
      return;
    }

    setCampaignBudgetInfo(prev => ({
      ...prev,
      pcBudget,
      mobBudget: prev.dailyAvgBudget - pcBudget,
      budgetRate:
          prev.dailyAvgBudget !== 0
              ? Math.round(pcBudget * 100 / prev.dailyAvgBudget)
              : 50
    }));
  };

  const handleChangeMobileBudget = value => {
    const numberValue = removeStr(value);

    const mobBudget =
        numberValue !== ""
            ? parseInt(numberValue, 10)
            : 0;

    if (!validateDailyBudget()) {
      return;
    }

    if (mobBudget > Number(campaignBudgetInfo.dailyAvgBudget)) {
      return;
    }

    setCampaignBudgetInfo(prev => ({
      ...prev,
      mobBudget,
      pcBudget: prev.dailyAvgBudget - mobBudget,
      budgetRate:
          prev.dailyAvgBudget !== 0
              ? 100 - Math.round(mobBudget * 100 / prev.dailyAvgBudget)
              : 50
    }));
  };

  const handleChangeInputRange = event => {
    if (campaignBudgetInfo.infiniteBudgetYn === "Y") {
      return;
    }

    if (!validateDailyBudget()) {
      return;
    }

    const budgetRate = parseInt(event.target.value, 10);

    setCampaignBudgetInfo(prev => ({
      ...prev,
      budgetRate,
      pcBudget: prev.dailyAvgBudget * budgetRate / 100,
      mobBudget:
          prev.dailyAvgBudget -
          prev.dailyAvgBudget * budgetRate / 100
    }));
  };

  const handleBudgetTime = () => {
    navigate("/board/budgetTimeList", {
      state: {
        id: userId
      }
    });
  };

  const handleChangeScheduleMode =
      async enabled => {
        if (!isEditMode) {
          toast.info(
              "캠페인 생성 완료 후 예약 설정을 사용할 수 있습니다.",
              {
                toastId:
                    "campaignBudgetScheduleEditOnly"
              }
          );

          return;
        }

        const hasActiveSchedule =
            scheduleHistory.some(
                isActiveSchedule
            );

        if (
            !enabled &&
            hasActiveSchedule
        ) {
          toast.info(
              "활성 예약을 모두 취소한 후 예약 설정을 OFF로 변경할 수 있습니다.",
              {
                toastId:
                    "activeBudgetScheduleExists"
              }
          );

          return;
        }

        try {
          await loadCurrentBudget();

          if (enabled) {
            await loadScheduleHistory();
          } else {
            setScheduleHistory([]);
          }

          setBudgetScheduleDraft(
              DEFAULT_BUDGET_SCHEDULE_DRAFT
          );

          clearErrors("scheduledAt");
          setIsScheduleMode(enabled);
        } catch (error) {
          console.error(
              "예산 예약 모드 변경 실패:",
              error
          );

          toast.error(
              "예산 정보를 불러오는 중 오류가 발생했습니다.",
              {
                toastId:
                    "changeCampaignBudgetScheduleModeError"
              }
          );
        }

      };

  const validateScheduleDraft = () => {
    clearErrors("scheduledAt");

    if (!isEditMode) {
      setError("scheduledAt", {
        type: "validate",
        message: "캠페인 생성 완료 후 예약할 수 있습니다."
      });

      return false;
    }

    if (!budgetScheduleDraft.scheduledAt) {
      setError("scheduledAt", {
        type: "required",
        message: "예약 적용 날짜를 선택해주세요."
      });

      return false;
    }

    const scheduledDate = toDateObject(
        budgetScheduleDraft.scheduledAt
    );

    const tomorrow = getTomorrow();

    if (
        !scheduledDate ||
        Number.isNaN(scheduledDate.getTime()) ||
        scheduledDate.getTime() < tomorrow.getTime()
    ) {
      setError("scheduledAt", {
        type: "validate",
        message: "내일 이후 날짜로 예약해주세요."
      });

      return false;
    }

    const duplicateSchedule = scheduleHistory.some(schedule => {
      if (!isActiveSchedule(schedule)) {
        return false;
      }

      const existingScheduleId = getScheduleId(schedule);

      if (
          existingScheduleId === budgetScheduleDraft.scheduleId
      ) {
        return false;
      }

      return (
          getScheduleDate(schedule) ===
          budgetScheduleDraft.scheduledAt
      );
    });

    if (duplicateSchedule) {
      setError("scheduledAt", {
        type: "validate",
        message: "같은 적용 날짜의 예약이 이미 존재합니다."
      });

      return false;
    }

    return true;
  };

  const handleSaveSchedule = async () => {
    if (
        isSavingSchedule ||
        !isScheduleMode
    ) {
      if (!isScheduleMode) {
        toast.info(
            "예약 설정을 ON으로 변경해주세요.",
            {
              toastId:
                  "campaignBudgetScheduleModeOff"
            }
        );
      }

      return;
    }

    if (
        !validateBudgetInfo() ||
        !validateScheduleDraft()
    ) {
      return;
    }

    setIsSavingSchedule(true);

    try {
      const response =
          await reserveCampaignBudget(
              campaignId,
              {
                scheduleId:
                budgetScheduleDraft
                    .scheduleId,
                scheduledAt:
                budgetScheduleDraft
                    .scheduledAt,
                infiniteBudgetYn:
                campaignBudgetInfo
                    .infiniteBudgetYn,
                dailyAvgBudget:
                    Number(
                        campaignBudgetInfo
                            .dailyAvgBudget ?? 0
                    ),
                pcBudget:
                    Number(
                        campaignBudgetInfo
                            .pcBudget ?? 0
                    ),
                mobBudget:
                    Number(
                        campaignBudgetInfo
                            .mobBudget ?? 0
                    ),
                budgetTimeId:
                campaignBudgetInfo
                    .budgetTimeId,
                biddingType:
                campaignBudgetInfo
                    .biddingType,
                biddingPrice:
                    Number(
                        campaignBudgetInfo
                            .biddingPrice ?? 0
                    ),
                maxBiddingPrice:
                    Number(
                        campaignBudgetInfo
                            .maxBiddingPrice ?? 0
                    )
              }
          );

      if (!response) {
        throw new Error(
            budgetScheduleDraft.scheduleId
                ? "예산 예약 수정 실패"
                : "예산 예약 추가 실패"
        );
      }

      toast.success(
          budgetScheduleDraft.scheduleId
              ? "예산 예약이 수정되었습니다."
              : "예산 예약이 추가되었습니다.",
          {
            autoClose: 800,
            toastId:
                "saveCampaignBudgetSchedule"
          }
      );

      setBudgetScheduleDraft(
          DEFAULT_BUDGET_SCHEDULE_DRAFT
      );

      await Promise.all([
        loadScheduleHistory(),
        loadCurrentBudget()
      ]);
    } catch (error) {
      console.error(
          "예산 예약 저장 실패:",
          error
      );

      toast.error(
          "예산 예약 처리 중 오류가 발생했습니다.",
          {
            toastId:
                "saveCampaignBudgetScheduleError"
          }
      );
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleEditSchedule = async schedule => {
    if (!isScheduleMode) {
      return;
    }

    const scheduleId = getScheduleId(schedule);

    setBudgetScheduleDraft({
      scheduleId,
      scheduledAt: getScheduleDate(schedule)
    });

    const scheduledBudget = {
      ...campaignBudgetInfo,
      infiniteBudgetYn: schedule.infiniteBudgetYn,
      dailyAvgBudget: schedule.dailyAvgBudget,
      pcBudget: schedule.pcBudget,
      mobBudget: schedule.mobBudget,
      budgetTimeId: schedule.budgetTimeId,
      biddingPrice: schedule.biddingPrice,
      biddingType: schedule.biddingType,
      maxBiddingPrice: schedule.maxBiddingPrice ?? 0,
      budgetRate:
          Number(schedule.dailyAvgBudget) !== 0
              ? Math.round(
                  Number(schedule.pcBudget) *
                  100 /
                  Number(schedule.dailyAvgBudget)
              )
              : 50
    };

    setCampaignBudgetInfo(scheduledBudget);
    reset(scheduledBudget);

    await loadBudgetTimeDetail(schedule.budgetTimeId);

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const handleResetSchedule = async () => {
    setBudgetScheduleDraft(DEFAULT_BUDGET_SCHEDULE_DRAFT);
    clearErrors("scheduledAt");
    await loadCurrentBudget();
  };

  const handleCancelSchedule =
      async schedule => {
        if (!isScheduleMode) {
          return;
        }

        const scheduleId =
            getScheduleId(schedule);

        if (!scheduleId) {
          return;
        }

        const confirmed =
            window.confirm(
                "해당 예산 예약을 취소하시겠습니까?"
            );

        if (!confirmed) {
          return;
        }

        try {
          const response =
              await cancelCampaignBudgetSchedule(
                  campaignId,
                  scheduleId
              );

          if (!response) {
            toast.error(
                "예약 취소 중 오류가 발생했습니다."
            );

            return;
          }

          toast.success(
              "예산 예약이 취소되었습니다.",
              {
                autoClose: 700,
                toastId:
                    "cancelCampaignBudgetSchedule"
              }
          );

          if (
              budgetScheduleDraft.scheduleId ===
              scheduleId
          ) {
            setBudgetScheduleDraft(
                DEFAULT_BUDGET_SCHEDULE_DRAFT
            );

            await loadCurrentBudget();
          }

          const schedules =
              await loadScheduleHistory();

          setIsScheduleMode(
              schedules.some(
                  isActiveSchedule
              )
          );
        } catch (error) {
          console.error(
              "예산 예약 취소 실패:",
              error
          );

          toast.error(
              "예약 취소 중 오류가 발생했습니다."
          );
        }

      };

  const onError = error => {
    console.log(error);
  };

  const onSubmit = async () => {
    if (
        isSavingBudget ||
        isSavingSchedule
    ) {
      return;
    }

    if (isScheduleMode) {
      toast.info(
          "예약 설정이 ON 상태입니다. 예약 추가 또는 예약 수정 버튼을 사용해주세요.",
          {
            toastId:
                "campaignBudgetScheduleSubmitBlocked"
          }
      );

      return;
    }

    if (
        budgetScheduleDraft.scheduleId ||
        budgetScheduleDraft.scheduledAt
    ) {
      toast.info(
          "예약 편집을 취소한 후 현재 예산을 수정해주세요.",
          {
            toastId:
                "budgetScheduleEditing"
          }
      );

      return;
    }

    if (!validateBudgetInfo()) {
      return;
    }

    const budgetParam = {
      ...campaignBudgetInfo,
      campaignId,
      targetingPriceId: 1
    };

    setIsSavingBudget(true);

    try {
      const response =
          await updateCampaignBudget(
              budgetParam
          );

      if (!response) {
        throw new Error(
            "현재 예산 저장 실패"
        );
      }

      resetGroupInfo();

      if (isEditMode) {
        if (
            campaignBudgetInfo
                .infiniteBudgetYn !==
            "Y" &&
            (
                Number(
                    campaignBudgetInfo
                        .pcBudget
                ) === 0 ||
                Number(
                    campaignBudgetInfo
                        .mobBudget
                ) === 0 ||
                budgetRateChk
            )
        ) {
          confirmAlert({
            title: "수정되었습니다",
            message:
                "예산 비율 설정에 따라 광고 그룹 및 크리에이티브 정보를 확인해주세요.",
            buttons: [
              {
                label: "확인",
                onClick: () => {
                  navigate(
                      "/board/dashboard",
                      {
                        replace: true
                      }
                  );
                }
              }
            ]
          });

          return;
        }

        toast.success(
            "현재 예산이 수정되었습니다.",
            {
              autoClose: 1000,
              toastId:
                  "updateCampaignBudget"
            }
        );

        window.setTimeout(() => {
          navigate(
              "/board/dashboard",
              {
                replace: true
              }
          );
        }, 700);

        return;
      }

      if (
          ![
            "STEP2_BUDGET",
            "STEP3_INVENTORY",
            "STEP4_CREATIVE",
            "COMPLETED"
          ].includes(
              campaignBasicInfo.step
          )
      ) {
        setCampaignBasicInfo(
            prev => ({
              ...prev,
              step:
                  "STEP2_BUDGET"
            })
        );
      }

      setStepCampaign({
        steps: 2
      });
    } catch (error) {
      console.error(
          "현재 예산 저장 실패:",
          error
      );

      toast.error(
          "예산 설정 처리 중 오류가 발생했습니다.",
          {
            toastId:
                "updateCampaignBudgetError"
          }
      );
    } finally {
      setIsSavingBudget(false);
    }
  };
  const BUDGET_RATIO_PRESETS = [
    {
      label: "0 : 100",
      value: 0
    },
    {
      label: "30 : 70",
      value: 30
    },
    {
      label: "50 : 50",
      value: 50
    },
    {
      label: "70 : 30",
      value: 70
    },
    {
      label: "100 : 0",
      value: 100
    },
  ];

  const handleSelectBudgetRatio = budgetRate => {
    if (
        campaignBudgetInfo.infiniteBudgetYn === "Y"
    ) {
      return;
    }

    if (!validateDailyBudget()) {
      return;
    }

    const dailyAvgBudget =
        Number(
            campaignBudgetInfo.dailyAvgBudget
        );

    const pcBudget =
        Math.round(
            dailyAvgBudget *
            budgetRate /
            100
        );

    setCampaignBudgetInfo(prev => ({
      ...prev,
      budgetRate,
      pcBudget,
      mobBudget:
          dailyAvgBudget - pcBudget
    }));
  };
  const selectedBudgetTimeLabel =
      budgetTimeListState.find(
          item =>
              item.value ===
              campaignBudgetInfo?.budgetTimeId
      )?.label ??
      timeBudgetDetailDataState?.groupName ??
      "선택된 시간별 예산 그룹이 없습니다.";

  const selectedBudgetTimeDetail =
      timeBudgetDetailDataState
          ?.exposureTimeType ??
      timeBudgetDetailDataState
      ?.timeGroups?.find(
          value =>
              value.id ===
              campaignBudgetInfo
                  ?.budgetTimeId
      );

  return (
      <CampaignStepForm
          onSubmit={handleSubmit(
              onSubmit,
              onError
          )}
      >
        {isEditMode && (
            <AdverInfo>
              <span>광고주 정보</span>
              <p />
              <span>
            {state?.adverInfo}
          </span>
            </AdverInfo>
        )}

        <Board>
          <BoardHeader>
            예산 및 입찰 설정
          </BoardHeader>

          <BoardSearchResult>
            <SettingCard>
              <SectionHeader>
                <SectionTitle>
                  예산 설정
                </SectionTitle>

                <SectionGuide>
                  일일 예산과 디바이스별 예산 비율을 설정합니다.
                </SectionGuide>
              </SectionHeader>

              <InnerBox>
                <FieldRow>
                  <FieldLabel>
                    일일 평균 예산
                  </FieldLabel>

                  <FieldContent>
                    <Inline>
                      <InputLabel label="원">
                        <SettingInput
                            type="text"
                            maxLength={13}
                            readOnly={
                                campaignBudgetInfo
                                    .infiniteBudgetYn !==
                                "N"
                            }
                            placeholder={
                              campaignBudgetInfo
                                  .infiniteBudgetYn !==
                              "Y"
                                  ? "일일 평균 예산을 설정해주세요."
                                  : ""
                            }
                            {...register(
                                "dailyAvgBudget",
                                {
                                  required:
                                      campaignBudgetInfo
                                          .infiniteBudgetYn !==
                                      "Y" &&
                                      "일일 평균 예산을 설정해주세요.",
                                  pattern: {
                                    value:
                                        /^[0-9,]+$/,
                                    message:
                                        "숫자만 입력 가능합니다."
                                  },
                                  onChange: event =>
                                      handleChangeDailyBudget(
                                          event.target
                                              .value
                                      )
                                }
                            )}
                            value={
                              campaignBudgetInfo
                                  .infiniteBudgetYn !==
                              "Y"
                                  ? decimalFormat(
                                      campaignBudgetInfo
                                          .dailyAvgBudget
                                  )
                                  : ""
                            }
                        />
                      </InputLabel>

                      <CustomCheckLabel>
                        <input
                            type="checkbox"
                            checked={
                                campaignBudgetInfo
                                    .infiniteBudgetYn !==
                                "N"
                            }
                            onChange={
                              handleCheckInfiniteBudget
                            }
                        />

                        <CheckboxMark />

                        <span>
                        일일 예산 무제한
                      </span>
                      </CustomCheckLabel>
                    </Inline>

                    {errors.dailyAvgBudget && (
                        <ValidationMessage>
                          {
                            errors.dailyAvgBudget
                                .message
                          }
                        </ValidationMessage>
                    )}
                  </FieldContent>
                </FieldRow>

                <FieldRow>
                  <FieldLabel>
                    예산 비율
                  </FieldLabel>

                  <FieldContent>
                    <BudgetRatioContainer>
                      <BudgetRatioCards>
                        <BudgetRatioCard>
                          <BudgetRatioHeader>
                            <BudgetDeviceName>
                              PC
                            </BudgetDeviceName>

                            <BudgetRatioPercent>
                              {campaignBudgetInfo
                                  .budgetRate ?? 50}
                              %
                            </BudgetRatioPercent>
                          </BudgetRatioHeader>

                          <InputLabel label="원">
                            <BudgetRatioInput
                                type="text"
                                readOnly={
                                    campaignBudgetInfo
                                        .infiniteBudgetYn !==
                                    "N"
                                }
                                {...register(
                                    "pcBudget",
                                    {
                                      pattern: {
                                        value:
                                            /^[0-9,]+$/,
                                        message:
                                            "숫자만 입력 가능합니다."
                                      },
                                      onChange:
                                          event =>
                                              handleChangePcBudget(
                                                  event
                                                      .target
                                                      .value
                                              )
                                    }
                                )}
                                value={
                                  campaignBudgetInfo
                                      .infiniteBudgetYn !==
                                  "Y"
                                      ? decimalFormat(
                                          campaignBudgetInfo
                                              .pcBudget
                                      )
                                      : ""
                                }
                            />
                          </InputLabel>
                        </BudgetRatioCard>

                        <BudgetRatioCard>
                          <BudgetRatioHeader>
                            <BudgetDeviceName>
                              MOBILE
                            </BudgetDeviceName>

                            <BudgetRatioPercent>
                              {100 -
                                  (campaignBudgetInfo
                                          .budgetRate ??
                                      50)}
                              %
                            </BudgetRatioPercent>
                          </BudgetRatioHeader>

                          <InputLabel label="원">
                            <BudgetRatioInput
                                type="text"
                                readOnly={
                                    campaignBudgetInfo
                                        .infiniteBudgetYn !==
                                    "N"
                                }
                                {...register(
                                    "mobBudget",
                                    {
                                      pattern: {
                                        value:
                                            /^[0-9,]+$/,
                                        message:
                                            "숫자만 입력 가능합니다."
                                      },
                                      onChange:
                                          event =>
                                              handleChangeMobileBudget(
                                                  event
                                                      .target
                                                      .value
                                              )
                                    }
                                )}
                                value={
                                  campaignBudgetInfo
                                      .infiniteBudgetYn !==
                                  "Y"
                                      ? decimalFormat(
                                          campaignBudgetInfo
                                              .mobBudget
                                      )
                                      : ""
                                }
                            />
                          </InputLabel>
                        </BudgetRatioCard>
                      </BudgetRatioCards>

                      <BudgetSliderArea>
                        <input
                            className={
                              campaignBudgetInfo
                                  .infiniteBudgetYn !==
                              "N"
                                  ? "read-only"
                                  : ""
                            }
                            type="range"
                            value={
                                campaignBudgetInfo
                                    .budgetRate ?? 50
                            }
                            disabled={
                                campaignBudgetInfo
                                    .infiniteBudgetYn !==
                                "N"
                            }
                            onChange={
                              handleChangeInputRange
                            }
                            style={{
                              background:
                                  `linear-gradient(
                              to right,
                              #f5811f 0%,
                              #f5811f ${campaignBudgetInfo.budgetRate ?? 50}%,
                              #ffd9be ${campaignBudgetInfo.budgetRate ?? 50}%,
                              #ffd9be 100%
                            )`
                            }}
                        />
                      </BudgetSliderArea>

                      <BudgetRatioPresetArea>
                        <BudgetRatioPresetTitle>
                          빠른 비율 설정
                        </BudgetRatioPresetTitle>

                        <BudgetRatioPresetButtons>
                          {BUDGET_RATIO_PRESETS.map(
                              preset => (
                                  <BudgetRatioPresetButton
                                      key={
                                        preset.value
                                      }
                                      type="button"
                                      $active={
                                          campaignBudgetInfo
                                              .budgetRate ===
                                          preset.value
                                      }
                                      disabled={
                                          campaignBudgetInfo
                                              .infiniteBudgetYn ===
                                          "Y"
                                      }
                                      onClick={() =>
                                          handleSelectBudgetRatio(
                                              preset.value
                                          )
                                      }
                                  >
                                    {preset.label}
                                  </BudgetRatioPresetButton>
                              )
                          )}
                        </BudgetRatioPresetButtons>
                      </BudgetRatioPresetArea>
                    </BudgetRatioContainer>
                  </FieldContent>
                </FieldRow>

                <FieldRow>
                  <FieldLabel>
                    시간별 예산 목록
                  </FieldLabel>

                  <FieldContent>
                    <BudgetTimeSelectRow>
                      <SelectWrap>
                        {budgetTimeListState.length >
                            0 && (
                                <Controller
                                    name="budgetTimeId"
                                    control={control}
                                    rules={{
                                      required: {
                                        value:
                                            !campaignBudgetInfo
                                                ?.budgetTimeId,
                                        message:
                                            "시간별 예산 그룹을 선택해주세요."
                                      }
                                    }}
                                    render={({field}) => (
                                        <Select
                                            {...field}
                                            options={
                                              budgetTimeListState
                                            }
                                            placeholder="시간별 예산 그룹 선택"
                                            value={
                                                budgetTimeListState.find(
                                                    item =>
                                                        item.value ===
                                                        campaignBudgetInfo
                                                            ?.budgetTimeId
                                                ) ?? null
                                            }
                                            onChange={
                                              handleChangeBudgetTimes
                                            }
                                            styles={
                                              selectComponentStyle
                                            }
                                        />
                                    )}
                                />
                            )}
                      </SelectWrap>

                      <BudgetListCreateButton
                          type="button"
                          onClick={
                            handleBudgetTime
                          }
                      >
                        예산 그룹 생성
                      </BudgetListCreateButton>
                    </BudgetTimeSelectRow>

                    {errors.budgetTimeId && (
                        <ValidationMessage>
                          {
                            errors.budgetTimeId
                                .message
                          }
                        </ValidationMessage>
                    )}

                    {campaignBudgetInfo?.budgetTimeId &&
                    selectedBudgetTimeDetail ? (
                        <TimeTable
                            exposureTimeType={
                              selectedBudgetTimeDetail
                            }
                            readOnly={true}
                            trigger={({open}) => (
                                <SelectedBudgetTimeBox
                                    type="button"
                                    onClick={open}
                                >
                                  <SelectedBudgetTimeContent>
                                    <SelectedBudgetTimeText>
                                      <SelectedBudgetTimeLabel>
                                        설정된 시간별 예산 그룹:
                                      </SelectedBudgetTimeLabel>

                                      <SelectedBudgetTimeValue>
                                        {selectedBudgetTimeLabel}
                                      </SelectedBudgetTimeValue>
                                    </SelectedBudgetTimeText>

                                    <SelectedBudgetTimeGuide>
                                      클릭하면 선택한 예산 그룹의
                                      시간대 설정을 확인할 수 있습니다.
                                    </SelectedBudgetTimeGuide>
                                  </SelectedBudgetTimeContent>

                                  <BudgetTimePopupIcon
                                      aria-hidden="true"
                                  >
                                    <svg
                                        width="13"
                                        height="13"
                                        viewBox="0 0 24 24"
                                    >
                                      <path
                                          fill="currentColor"
                                          d="M14 3h7v7h-2V6.41l-8.29 8.3-1.42-1.42 8.3-8.29H14V3ZM5 5h6v2H7v10h10v-4h2v6H5V5Z"
                                      />
                                    </svg>
                                  </BudgetTimePopupIcon>
                                </SelectedBudgetTimeBox>
                            )}
                        />
                    ) : (
                        <SelectedBudgetTimeBox
                            type="button"
                            disabled
                        >
                          <SelectedBudgetTimeContent>
                            <SelectedBudgetTimeText>
                              <SelectedBudgetTimeLabel>
                                설정된 시간별 예산 그룹:
                              </SelectedBudgetTimeLabel>

                              <SelectedBudgetTimeValue>
                                선택된 시간별 예산 그룹이 없습니다.
                              </SelectedBudgetTimeValue>
                            </SelectedBudgetTimeText>

                            <SelectedBudgetTimeGuide>
                              시간별 예산 그룹을 먼저 선택해주세요.
                            </SelectedBudgetTimeGuide>
                          </SelectedBudgetTimeContent>

                          <BudgetTimePopupIcon
                              aria-hidden="true"
                          >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                            >
                              <path
                                  fill="currentColor"
                                  d="M14 3h7v7h-2V6.41l-8.29 8.3-1.42-1.42 8.3-8.29H14V3ZM5 5h6v2H7v10h10v-4h2v6H5V5Z"
                              />
                            </svg>
                          </BudgetTimePopupIcon>
                        </SelectedBudgetTimeBox>
                    )}

                  </FieldContent>
                </FieldRow>
              </InnerBox>
            </SettingCard>

            <SettingCard>
              <SectionHeader>
                <SectionTitle>
                  입찰 설정
                </SectionTitle>

                <SectionGuide>
                  입찰 방식과 광고 노출 단가를 설정합니다.
                </SectionGuide>
              </SectionHeader>

              <InnerBox>
                <FieldRow>
                  <FieldLabel>
                    입찰 방식
                  </FieldLabel>

                  <FieldContent>
                    <SelectWrap>
                      <Select
                          styles={
                            selectComponentStyle
                          }
                          components={{
                            IndicatorSeparator:
                                () => null
                          }}
                          options={biddingType}
                          value={
                              biddingType.find(
                                  item =>
                                      item.value ===
                                      campaignBudgetInfo
                                          ?.biddingType
                              ) ?? null
                          }
                          onChange={
                            handleBiddingType
                          }
                      />
                    </SelectWrap>
                  </FieldContent>
                </FieldRow>

                <FieldRow>
                  <FieldLabel>
                    단가 설정
                  </FieldLabel>

                  <FieldContent>
                    <InputLabel label="원">
                      <SettingInput
                          type="text"
                          maxLength={13}
                          placeholder="단가를 입력해주세요."
                          {...register(
                              "biddingPrice",
                              {
                                required:
                                    !campaignBudgetInfo
                                        .biddingPrice &&
                                    "단가를 입력해주세요.",
                                pattern: {
                                  value:
                                      /^[0-9,]+$/,
                                  message:
                                      "숫자만 입력 가능합니다."
                                },
                                onChange:
                                    event =>
                                        handleChangePriceEvent(
                                            event.target
                                                .value
                                        )
                              }
                          )}
                          value={
                            campaignBudgetInfo
                                .biddingPrice
                                ? decimalFormat(
                                    campaignBudgetInfo
                                        .biddingPrice
                                )
                                : ""
                          }
                      />
                    </InputLabel>

                    {errors.biddingPrice && (
                        <ValidationMessage>
                          {
                            errors.biddingPrice
                                .message
                          }
                        </ValidationMessage>
                    )}
                  </FieldContent>
                </FieldRow>
              </InnerBox>
            </SettingCard>

            <BudgetScheduleSetting
                isEditMode={isEditMode}
                isScheduleMode={
                  isScheduleMode
                }
                onChangeScheduleMode={
                  handleChangeScheduleMode
                }
                budgetScheduleDraft={
                  budgetScheduleDraft
                }
                setBudgetScheduleDraft={
                  setBudgetScheduleDraft
                }
                scheduleHistory={
                  scheduleHistory
                }
                errors={errors}
                clearErrors={clearErrors}
                onSaveSchedule={
                  handleSaveSchedule
                }
                onEditSchedule={
                  handleEditSchedule
                }
                onCancelSchedule={
                  handleCancelSchedule
                }
                onResetSchedule={
                  handleResetSchedule
                }
                isSavingSchedule={
                  isSavingSchedule
                }
            />
          </BoardSearchResult>
        </Board>

        <CampaignStepSidebar>
          <CampaignSettingSummary currentStep={1} />
          <SubmitContainer>
          <CancelButton
              type="button"
              disabled={
                  isSavingBudget ||
                  isSavingSchedule
              }
              onClick={() => {
                if (isEditMode) {
                  navigate(-1);
                  return;
                }

                setStepCampaign({
                  steps: 0
                });
              }}
          >
            {isEditMode
                ? "목록"
                : "이전"}
          </CancelButton>

          {!isScheduleMode ? (
              <SaveButton
                  type="submit"
                  disabled={
                      isSavingBudget ||
                      isSavingSchedule
                  }
              >
                {isSavingBudget
                    ? "처리 중"
                    : isEditMode
                        ? "현재 예산 수정"
                        : "다음[2/4]"}
              </SaveButton>
          ) : (
              <ScheduleSubmitGuide>
                예약 추가·수정은 위 예약 설정에서 진행해주세요.
              </ScheduleSubmitGuide>
          )}
          </SubmitContainer>
        </CampaignStepSidebar>
      </CampaignStepForm>
  );
}


const SettingCard = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: var(--font-size-17);
  font-weight: 700;
`;

const SectionGuide = styled.div`
  color: #888;
  font-size: var(--font-size-12);
  line-height: 1.5;
  text-align: right;
`;

const InnerBox = styled.div`
  padding: 22px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FieldLabel = styled.div`
  width: 150px;
  min-width: 150px;
  padding-top: 10px;
  color: #444;
  font-size: var(--font-size-16);
  font-weight: 600;

  @media (max-width: 900px) {
    width: auto;
    min-width: 0;
    padding-top: 0;
  }
`;

const FieldContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 10px;
`;

const Inline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SettingInput = styled(Input)`
  width: 100%;
  min-width: 340px;
  max-width: 460px;
  height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;

  &:focus {
    border-color: #f5811f;
  }

  &:read-only {
    background-color: #f5f5f5;
    color: #999;
  }

  &::placeholder {
    color: #aaa;
  }

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const ValidationMessage = styled.div`
  color: #e03131;
  font-size: var(--font-size-13);
  line-height: 1.4;
`;

const CustomCheckLabel = styled.label`
  position: relative;
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 7px;
  color: #555;
  font-size: var(--font-size-14);
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  input:checked + span {
    border-color: #f5811f;
    background-color: #f5811f;
  }

  input:checked + span::after {
    display: block;
  }
`;

const CheckboxMark = styled.span`
  position: relative;
  width: 17px;
  height: 17px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 2px;
    left: 5px;
    display: none;
    width: 4px;
    height: 8px;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    content: "";
    transform: rotate(45deg);
  }
`;

const BudgetRatioContainer = styled.div`
  width: 100%;
  max-width: 850px;
`;

const BudgetRatioCards = styled.div`
  display: grid;
  grid-template-columns:
    repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const BudgetRatioCard = styled.div`
  padding: 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
`;

const BudgetRatioHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const BudgetDeviceName = styled.span`
  color: #555;
  font-size: var(--font-size-15);
  font-weight: 600;
`;

const BudgetRatioPercent = styled.span`
  color: #f5811f;
  font-size: var(--font-size-20);
  font-weight: 700;
`;

const BudgetRatioInput = styled(Input)`
  width: 100%;
  height: 38px;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  background-color: #fafafa;

  &:focus {
    border-color: #f5811f;
    background-color: #fff;
  }

  &:read-only {
    background-color: #f5f5f5;
    color: #aaa;
  }
`;

const BudgetSliderArea = styled.div`
  display: flex;
  align-items: center;
  margin-top: 14px;
  padding: 0 6px;

  input[type="range"] {
    width: 100%;
    height: 10px;
    border-radius: 999px;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
  }

  input[type="range"]::-webkit-slider-thumb {
    width: 18px;
    height: 18px;
    border: 2px solid #f5811f;
    border-radius: 50%;
    background-color: #fff;
    cursor: pointer;
    -webkit-appearance: none;
  }

  input[type="range"]::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border: 2px solid #f5811f;
    border-radius: 50%;
    background-color: #fff;
    cursor: pointer;
  }

  input[type="range"]:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const BudgetRatioPresetArea = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const BudgetRatioPresetTitle = styled.span`
  color: #777;
  font-size: var(--font-size-14);
`;

const BudgetRatioPresetButtons = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
`;

const BudgetRatioPresetButton = styled.button`
  min-width: 70px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid
    ${({$active}) =>
    $active
        ? "#f5811f"
        : "#e1e1e1"};
  border-radius: 15px;
  background-color:
    ${({$active}) =>
    $active
        ? "#fff4e8"
        : "#fff"};
  color:
    ${({$active}) =>
    $active
        ? "#f5811f"
        : "#666"};
  font-size: var(--font-size-14);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #f5811f;
    color: #f5811f;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #bbb;
    cursor: not-allowed;
  }
`;

const SelectWrap = styled.div`
  width: 100%;
  min-width: 340px;
  max-width: 460px;

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const SubActionButton = styled.button`
  height: 40px;
  padding: 0 15px;
  border: 1px solid #f1d4c2;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-14);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #ffe8d2;
  }
`;

const SubSettingBox = styled.div`
  width: 100%;
  margin-top: 4px;
  padding: 16px 18px;
  border: 1px solid #e5e5e5;
  border-left: 3px solid #f5811f;
  border-radius: 0 7px 7px 0;
  background-color: #fff;
`;

const SubSettingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
`;

const SubSettingTitle = styled.div`
  margin-bottom: 4px;
  color: #444;
  font-size: var(--font-size-14);
  font-weight: 700;
`;

const SubSettingDescription = styled.div`
  color: #999;
  font-size: var(--font-size-13);
  line-height: 1.4;
`;

const SubSettingDivider = styled.div`
  height: 1px;
  margin: 16px 0;
  background-color: #eeeeee;
`;

const TimePreviewBox = styled.div`
  overflow-x: auto;
  padding-top: 2px;

  & > div {
    border-radius: 6px;
    background-color: #fafafa;
  }
`;

const ScheduleModeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ScheduleModeButton = styled.button`
  position: relative;
  width: 84px;
  height: 34px;
  flex: 0 0 84px;
  padding: 3px;
  border: 0;
  border-radius: 18px;
  background-color:
    ${({$active}) =>
    $active
        ? "#f5811f"
        : "#b8b8b8"};
  cursor: pointer;
  transition:
    background-color 0.2s ease;

  &:disabled {
    background-color: #d9d9d9;
    cursor: not-allowed;
  }
`;

const ScheduleModeKnob = styled.span`
  position: absolute;
  top: 3px;
  left: 3px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow:
    0 1px 3px
    rgba(0, 0, 0, 0.25);
  transform:
    translateX(
      ${({$active}) =>
    $active
        ? "50px"
        : "0"}
    );
  transition:
    transform 0.2s ease;
`;

const ScheduleModeText = styled.span`
  position: absolute;
  top: 50%;
  ${({$active}) =>
    $active
        ? "left: 13px;"
        : "right: 12px;"}
  color: #fff;
  font-size: var(--font-size-14);
  font-weight: 700;
  transform: translateY(-50%);
`;

const ScheduleDescription = styled.span`
  color: #888;
  font-size: var(--font-size-14);
  line-height: 1.5;
`;

const EditingBadge = styled.span`
  display: inline-flex;
  min-height: 24px;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 12px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-12);
  font-weight: 700;
`;

const ScheduleEditorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ScheduleDatePickerWrap = styled.div`
  width: 100%;
  min-width: 340px;
  max-width: 460px;

  ${DateContainer} {
    width: 360px;
    max-width: 100%;
  }

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: 100%;
  }

  input {
    width: 100%;
    height: 40px;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    background-color: #fff;

    &:focus {
      border-color: #f5811f;
    }
  }

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const ScheduleButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const SchedulePrimaryButton = styled.button`
  min-width: 80px;
  height: 36px;
  padding: 0 13px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-14);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const ScheduleSecondaryButton = styled.button`
  min-width: 54px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #fff;
  color: #555;
  font-size: var(--font-size-13);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #f5811f;
    background-color: #fffaf6;
    color: #f5811f;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const ScheduleListHeader = styled.div`
  display: flex;
  min-height: 28px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 11px;
`;

const SelectedCount = styled.span`
  display: inline-flex;
  min-height: 24px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 12px;
  background-color: #fff;
  color: #f5811f;
  font-size: var(--font-size-12);
  font-weight: 700;
`;

const ScheduleHistoryBox = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
`;

const ScheduleHeader = styled.div`
  display: grid;
  grid-template-columns:
    1.1fr
    1fr
    1.4fr
    0.9fr
    0.9fr
    1fr;
  min-width: 820px;
  min-height: 42px;
  align-items: center;
  padding: 0 15px;
  border-bottom: 1px solid #e5e5e5;
  background-color: #f7f7f7;
  color: #444;
  font-size: var(--font-size-14);
  font-weight: 600;
`;

const ScheduleBody = styled.div`
  max-height: calc(52px * 4);
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color:
    #c7c7c7 transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background-color: #c7c7c7;
  }
`;

const ScheduleRow = styled.div`
  display: grid;
  grid-template-columns:
    1.1fr
    1fr
    1.4fr
    0.9fr
    0.9fr
    1fr;
  min-width: 820px;
  min-height: 52px;
  align-items: center;
  padding: 0 15px;
  border-bottom: 1px solid #eee;
  color: #555;
  font-size: var(--font-size-14);

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: #fffaf6;
  }
`;

const ScheduleEmpty = styled.div`
  min-width: 820px;
  padding: 30px 15px;
  color: #999;
  font-size: var(--font-size-14);
  text-align: center;
`;

const ScheduleStatus = styled.span`
  display: inline-flex;
  min-width: 72px;
  align-items: center;
  justify-content: center;
  padding: 5px 8px;
  border-radius: 12px;
  background-color:
    ${({$status}) => {
  switch ($status) {
    case "WAITING":
      return "#fff4e8";
    case "PROCESSING":
      return "#e8f2ff";
    case "APPLIED":
      return "#eaf7ee";
    case "CANCELLED":
      return "#f2f2f2";
    case "FAILED":
      return "#fff0f0";
    default:
      return "#f2f2f2";
  }
}};
  color:
    ${({$status}) => {
  switch ($status) {
    case "WAITING":
      return "#f5811f";
    case "PROCESSING":
      return "#1971c2";
    case "APPLIED":
      return "#2b8a3e";
    case "CANCELLED":
      return "#888";
    case "FAILED":
      return "#e03131";
    default:
      return "#555";
  }
}};
  font-size: var(--font-size-12);
  font-weight: 600;
`;

const ScheduleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ScheduleSubmitGuide = styled.span`
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  color: #777;
  font-size: var(--font-size-14);
`;

const SaveButton = styled.button`
  min-width: 104px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-15);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const selectComponentStyle = {
  control: (
      provided,
      state
  ) => ({
    ...provided,
    minHeight: "40px",
    borderRadius: "6px",
    border:
        state.isFocused
            ? "1px solid #f5811f"
            : "1px solid #e5e5e5",
    backgroundColor: "#fff",
    boxShadow: "none",
    "&:hover": {
      border:
          "1px solid #f5811f"
    }
  }),
  valueContainer: provided => ({
    ...provided,
    padding: "0 12px"
  }),
  placeholder: provided => ({
    ...provided,
    color: "#999"
  }),
  singleValue: provided => ({
    ...provided,
    color: "#444"
  }),
  menu: provided => ({
    ...provided,
    zIndex: 20
  })
};


const BudgetTimeSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BudgetListCreateButton = styled.button`
  height: 40px;
  padding: 0 16px;
  border: 1px solid #f1d4c2;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: var(--font-size-14);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #ffe8d2;
  }
`;

const SelectedBudgetTimeBox = styled.button`
  display: flex;
  width: 100%;
  min-height: 62px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 14px;
  border: 1px solid #e5e5e5;
  border-left: 3px solid #f5811f;
  border-radius: 0 6px 6px 0;
  background-color: #fff;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #f1d4c2;
    border-left-color: #f5811f;
    background-color: #fffaf6;
  }

  &:disabled {
    border-left-color: #ccc;
    background-color: #f7f7f7;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 1px solid #f5811f;
    outline-offset: 2px;
  }
`;

const SelectedBudgetTimeContent = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

const SelectedBudgetTimeText = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
`;

const SelectedBudgetTimeLabel = styled.span`
  flex: 0 0 auto;
  color: #777;
  font-size: var(--font-size-14);
`;

const SelectedBudgetTimeValue = styled.span`
  overflow: hidden;
  max-width: 520px;
  color: #333;
  font-size: var(--font-size-14);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedBudgetTimeGuide = styled.span`
  color: #999;
  font-size: var(--font-size-12);
  line-height: 1.4;
`;

const BudgetTimePopupIcon = styled.span`
  display: inline-flex;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #fff4e8;
  color: #f5811f;

  ${SelectedBudgetTimeBox}:disabled & {
    background-color: #eee;
    color: #aaa;
  }
`;

const ScheduleControlRow = styled.div`
  display: flex;
  min-height: 40px;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FieldLabelDescription = styled.span`
  display: block;
  margin-top: 6px;
  color: #999;
  font-size: var(--font-size-12);
  font-weight: 400;
  line-height: 1.45;
`;

const ScheduleListTop = styled.div`
  display: flex;
  min-height: 28px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
